/* eslint-disable no-bitwise */

export type Hash128Bit = [number, number, number, number];

export const isBetween = (num1: number, num2: number, num3: number, opts?: { inclusive?: boolean }) => {
  const { inclusive = false } = opts ?? {};
  if (inclusive) {
    return num1 >= num2 && num1 <= num3;
  }
  return num1 > num2 && num1 < num3;
};

export const findClosestNumber = (arr: number[], target: number) => {
  return arr.reduce((prev, curr) => {
    return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
  });
};

/**
 * Produces a 128bit hash from a string
 *
 * @see {@link https://stackoverflow.com/questions/521295/seeding-the-random-number-generator-in-javascript}
 */
export function cyrb128(str: string): Hash128Bit {
  // Instantiate some numbers with arbitrary large values
  let h1 = 1779033703,
    h2 = 3144134277,
    h3 = 1013904242,
    h4 = 2773480762;

  // Step over the characters in the string,
  for (let i = 0, k; i < str.length; i++) {
    k = str.charCodeAt(i);
    // Math.imul uses 32-bit integer multiplication.
    // We combine it with bitwise XOR `^` and arbitrary large numbers to get
    // deterministically shuffled result integers.
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }

  // Here we use both itwise XOR `^` and Zero-Fill Right Shift `>>>` with more
  // multiplication against arbitrary large numbers to get more
  // deterministically shuffled result integers.
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);

  // Then we combine the results together with XOR and ZFRS to get a hash of
  // four unique integers that are deterministic for the input string.
  return [(h1 ^ h2 ^ h3 ^ h4) >>> 0, (h2 ^ h1) >>> 0, (h3 ^ h1) >>> 0, (h4 ^ h1) >>> 0];
}

/**
 * A fast random number generator that supports a 128bit seed for deterministic
 * random number creation
 *
 * @see {@link https://stackoverflow.com/questions/521295/seeding-the-random-number-generator-in-javascript}
 *
 * #Example
 * ```
 * import { cyrb128 } from '@inkitt/utils-random';
 *
 * const seed = cyrb128('some string'); // Generate a fixed 128bit hash seed
 *
 * const generateRandomNumber = simpleFastCounter(seed);
 *
 * const randomNumbers = new Array(10).fill(0).map(() => generateRandomNumber());
 * ```
 */
export function simpleFastCounter([a, b, c, d]: Hash128Bit): () => number {
  return (): number => {
    // @note: We're assigning to our arguments here, and because they're held in the
    //        upper scope they'll persist between calls to the function.
    a >>>= 0; // zero-fill right shift rounds `number` to Int32.
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;

    let result = (a + b) | 0; // starts by using bitwise OR 0 to flip bits

    a = b ^ (b >>> 9); // bitwise XOR of zero-fill right shift, shuffles bits
    b = (c + (c << 3)) | 0; // adds left shifted number, flips bits again
    c = (c << 21) | (c >>> 11); // uses bitwise OR to shuffle bits again
    d = (d + 1) | 0; // uses bitwise OR 0 to flip bits
    result = (result + d) | 0; // so much bit flipping.
    c = (c + result) | 0; // flippy bits, everywhere.

    // Finally we'll zero-fill shift right by 0 to ensure it's still an Int32
    // and we'll divide by `4294967296` (our maximum after transformation) to bring
    // the result down to a value between 0 and 1.
    return (result >>> 0) / 4294967296;
  };
}
